"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const react_2 = require("@floating-ui/react");
const oxygen_static_tooltip_1 = __importDefault(require("@8x8/oxygen-static-tooltip"));
const oxygen_config_1 = require("@8x8/oxygen-config");
const oxygen_utils_1 = require("@8x8/oxygen-utils");
const utils_1 = require("../utils");
const Arrow_1 = __importDefault(require("./Arrow"));
const types_1 = require("../types");
const side = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
};
const Tooltip = ({ children, customCloseHandlers = [], delay = 0, enableArrow, includeWrapper = true, offset = 7, modifiers, orientation = types_1.orientation.top, renderContainer, renderContainerId, showOn = types_1.showOn.hover, disableInteractive = false, testId = 'TOOLTIP', theme, title, triggerRef, forwardedRef, ...rest }) => {
    const [open, setOpen] = (0, react_1.useState)(false);
    // @deprecated modifiers — use offset instead directly
    const computedOffset = (0, utils_1.parseModifiers)(modifiers) || offset;
    const handlers = react_1.default.useCallback(() => customCloseHandlers.map(handler => ({
        ...handler,
        callback: () => setOpen(false),
    })), [customCloseHandlers])();
    // @deprecated customCloseHandlers — we'll keep it if we find a use-case and usage
    (0, react_1.useEffect)(() => {
        if (open) {
            (0, utils_1.registerEventHandlers)(handlers);
        }
        else {
            (0, utils_1.unregisterEventHandlers)(handlers);
        }
        return () => (0, utils_1.unregisterEventHandlers)(handlers);
    }, [open]);
    const arrowRef = (0, react_1.useRef)(null);
    const { x, y, refs, strategy, context, placement, middlewareData: { arrow: { x: arrowX, y: arrowY } = {} }, } = (0, react_2.useFloating)({
        open,
        onOpenChange: setOpen,
        placement: orientation,
        middleware: [
            (0, react_2.offset)(computedOffset),
            (0, react_2.flip)(),
            (0, react_2.shift)({ limiter: (0, react_2.limitShift)() }),
            (0, react_2.arrow)({ element: arrowRef, padding: 9 }),
        ],
        whileElementsMounted: react_2.autoUpdate,
    });
    // Use external HTMLElement as trigger if provided
    (0, react_1.useLayoutEffect)(() => {
        if (!triggerRef)
            return;
        refs.setReference(triggerRef);
    }, [refs.setReference, triggerRef]);
    // Merge all the interactions into prop getters
    const { getReferenceProps, getFloatingProps } = (0, react_2.useInteractions)([
        (0, react_2.useHover)(context, {
            enabled: showOn === 'hover',
            move: false,
            delay: {
                open: delay,
                close: 0,
            },
            handleClose: disableInteractive
                ? // closes the toolip when the mouse leaves the reference
                    null
                : // helps keep the tooltip open when moving the mouse from the trigger to the floating element
                    (0, react_2.safePolygon)({ blockPointerEvents: false }),
        }),
        (0, react_2.useClick)(context, { enabled: showOn === 'click', toggle: true }),
        // Only use focus on hover enabled Tooltips
        (0, react_2.useFocus)(context, { enabled: showOn === 'hover' }),
        // Role props for screen readers
        (0, react_2.useRole)(context, { role: 'tooltip' }),
        (0, react_2.useDismiss)(context, {
            escapeKey: true,
            // only enable reference press dismiss on hover (otherwise this breaks useClick => toggle: true)
            referencePress: showOn === 'hover',
        }),
    ]);
    const staticSide = side[placement.split('-')[0]];
    const renderWithoutWrapper = () => react_1.default.Children.map(children || null, (child, index) => {
        // only use the first child as target
        if (index === 0 && react_1.default.isValidElement(child)) {
            return (0, react_1.cloneElement)(child, getReferenceProps({
                ref: (0, react_2.useMergeRefs)([refs.setReference, forwardedRef]),
                ...child.props,
                ...rest,
            }));
        }
        return child;
    });
    const renderWithWrapper = () => (react_1.default.createElement("div", { ...getReferenceProps({
            ref: (0, react_2.useMergeRefs)([refs.setReference, forwardedRef]),
            ...(0, oxygen_config_1.getTestAttributes)(`ANCHOR_FOR_${testId}`),
            ...rest,
        }) }, children));
    const generatedId = (0, oxygen_utils_1.useId)();
    /**
     * ID priority:
     * 1. specified `renderContainerId` prop
     * 2. if `renderContainer` is provided: use a generated UUID
     * 3. use the default static value 'oxygen-tooltip-root'
     *
     * (note: need to keep the ternary separate from || shorcircuit below, otherwise it always evaluates to the ternary result)
     */
    const id = renderContainer ? generatedId : 'oxygen-tooltip-root';
    const floatingPortalProps = {
        id: renderContainerId || id,
        ...(renderContainer && { root: renderContainer }),
    };
    return (react_1.default.createElement(react_1.default.Fragment, null,
        includeWrapper ? renderWithWrapper() : renderWithoutWrapper(),
        react_1.default.createElement(react_2.FloatingPortal, { ...floatingPortalProps }, open && (react_1.default.createElement("div", { ref: refs.setFloating, style: {
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
                width: 'max-content',
            }, ...getFloatingProps() },
            react_1.default.createElement(oxygen_static_tooltip_1.default, { theme: theme, testId: `STATIC_TOOLTIP_FOR_${testId}`, renderArrow: () => (enableArrow ?? theme.enableArrow) && (react_1.default.createElement(Arrow_1.default, { ref: arrowRef, ...(0, oxygen_config_1.getTestAttributes)(`tooltip_arrow_${testId}`), style: {
                        left: arrowX != null ? `${arrowX}px` : '',
                        top: arrowY != null ? `${arrowY}px` : '',
                        [staticSide]: theme.arrowMarginOffset,
                    } })), ...rest }, title))))));
};
exports.default = (0, oxygen_config_1.withDeprecation)((0, oxygen_config_1.withTheme)(Tooltip, 'staticTooltip'), `Tooltip deprecated props: "modifiers", "customCloseHandlers"`);
